/*
 * The page/link graph.
 *
 * A graph view earns its place by answering two questions at a glance that no list
 * answers: where the hubs are, and what is hanging off nothing. So node radius is
 * inbound-link count and an orphan is drawn as a dashed outline rather than being
 * listed somewhere else.
 *
 * The interaction is what makes it usable rather than decorative, and it is borrowed
 * from prior art: clicking a node dims everything outside its neighbourhood to about
 * a tenth, and the search box dims by title match live. Without focus, a graph past
 * fifty nodes is a hairball you can look at and not read.
 *
 * The layout is a plain O(n²) spring simulation, which is the right call at this
 * size: a knowledge base with hundreds of pages settles in a few hundred ticks, and
 * a quadtree would be more code than the whole component.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { LinkGraph } from '../../../api/knowledgeBases.ts'
import { baseName } from '../lib/format.ts'

const WIDTH = 1600
const HEIGHT = 720

/** Ticks before the layout is called settled. Enough to unfold, cheap to run. */
const TICKS = 420
/*
 * Tuned so the layout USES the frame. A stronger centering pull with short springs
 * settles into a tight ball in the middle of a wide canvas — technically a correct
 * layout, and unreadable, with most of the pixels doing nothing.
 */
const REPULSION = 11_000
const SPRING = 0.010
const SPRING_LENGTH = 170
const CENTERING = 0.0009
const DAMPING = 0.86
/** Opacity everything outside the focused neighbourhood drops to. */
const DIMMED = 0.1

interface Node {
  id: string
  x: number
  y: number
  vx: number
  vy: number
  inbound: number
  orphan: boolean
  broken: boolean
  group: string
}

interface Edge {
  from: number
  to: number
}

interface ForceGraphProps {
  links: LinkGraph
  selected: string | null
  onSelect: (path: string | null) => void
  query: string
}

const GROUP_COLOURS: Record<string, string> = {
  concepts: '#c2643f',
  entities: '#6d9ad4',
  sources: '#5c5c62',
  syntheses: '#34d399',
  root: '#8f8f97',
}

export function ForceGraph({ links, selected, onSelect, query }: ForceGraphProps) {
  const { nodes: seedNodes, edges, index } = useMemo(() => buildGraph(links), [links])
  const [nodes, setNodes] = useState<Node[]>(seedNodes)
  const nodesRef = useRef<Node[]>(seedNodes)
  const dragging = useRef<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  // Re-seed whenever the graph itself changes, not on every render.
  useEffect(() => {
    nodesRef.current = seedNodes.map((node) => ({ ...node }))
    setNodes(nodesRef.current)
  }, [seedNodes])

  useEffect(() => {
    let frame = 0
    let tick = 0
    const step = () => {
      simulate(nodesRef.current, edges, dragging.current)
      setNodes(nodesRef.current.map((node) => ({ ...node })))
      tick += 1
      if (tick < TICKS) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [seedNodes, edges])

  const neighbourhood = useMemo(() => {
    if (selected === null) return null
    const centre = index.get(selected)
    if (centre === undefined) return null
    const near = new Set<number>([centre])
    for (const edge of edges) {
      if (edge.from === centre) near.add(edge.to)
      if (edge.to === centre) near.add(edge.from)
    }
    return near
  }, [selected, edges, index])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return null
    const hits = new Set<number>()
    nodesRef.current.forEach((node, position) => {
      if (node.id.toLowerCase().includes(needle)) hits.add(position)
    })
    return hits
  }, [query, nodes.length])

  const opacityOf = useCallback(
    (position: number): number => {
      if (matches !== null && !matches.has(position)) return DIMMED
      if (neighbourhood !== null && !neighbourhood.has(position)) return DIMMED
      return 1
    },
    [matches, neighbourhood],
  )

  function pointerPosition(event: React.PointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current
    if (!svg) return null
    const rect = svg.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * HEIGHT,
    }
  }

  return (
    <svg
      ref={svgRef}
      className="graph__canvas"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label="Граф страниц и ссылок"
      onPointerMove={(event) => {
        const at = dragging.current === null ? null : pointerPosition(event)
        if (at === null || dragging.current === null) return
        const node = nodesRef.current[dragging.current]
        if (!node) return
        node.x = at.x
        node.y = at.y
        node.vx = 0
        node.vy = 0
        setNodes(nodesRef.current.map((entry) => ({ ...entry })))
      }}
      onPointerUp={() => {
        dragging.current = null
      }}
      onPointerLeave={() => {
        dragging.current = null
      }}
    >
      <g stroke="rgba(255,255,255,.13)" strokeWidth="1" fill="none">
        {edges.map((edge, position) => {
          const from = nodes[edge.from]
          const to = nodes[edge.to]
          if (!from || !to) return null
          const lit =
            neighbourhood === null ||
            (neighbourhood.has(edge.from) && neighbourhood.has(edge.to))
          return (
            <line
              key={position}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              opacity={lit ? 1 : DIMMED}
            />
          )
        })}
      </g>
      <g>
        {nodes.map((node, position) => {
          const radius = 6 + Math.min(20, Math.sqrt(node.inbound) * 5)
          const opacity = opacityOf(position)
          const colour = GROUP_COLOURS[node.group] ?? GROUP_COLOURS.root
          return (
            <g key={node.id} opacity={opacity}>
              <circle
                className="graph__node"
                cx={node.x}
                cy={node.y}
                r={radius}
                fill={node.orphan ? 'none' : colour}
                stroke={node.orphan ? '#f87171' : node.broken ? '#e0a23f' : 'none'}
                strokeWidth={node.orphan || node.broken ? 1.5 : 0}
                strokeDasharray={node.orphan ? '3 3' : undefined}
                onPointerDown={(event) => {
                  event.preventDefault()
                  dragging.current = position
                }}
                onClick={() => onSelect(node.id === selected ? null : node.id)}
              >
                <title>
                  {node.id} · входящих: {node.inbound}
                  {node.orphan ? ' · сирота' : ''}
                </title>
              </circle>
              {radius >= 9 || opacity === 1 ? (
                <text
                  x={node.x}
                  y={node.y + radius + 13}
                  textAnchor="middle"
                  fill={node.orphan ? '#f0a5a5' : '#c8c8cd'}
                  fontSize={radius >= 12 ? 12 : 11}
                  fontWeight={radius >= 14 ? 600 : 400}
                >
                  {baseName(node.id).replace(/\.md$/, '')}
                </text>
              ) : null}
            </g>
          )
        })}
      </g>
    </svg>
  )
}

function buildGraph(links: LinkGraph): {
  nodes: Node[]
  edges: Edge[]
  index: Map<string, number>
} {
  const pages = links.pages ?? []
  const orphans = new Set(links.orphans ?? [])
  // A broken link names the PAGE it sits in as `source`; the node tinted is the
  // page that needs fixing, not the target that does not exist.
  const broken = new Set((links.broken ?? []).map((link) => link.source))
  const index = new Map<string, number>()
  const nodes: Node[] = pages.map((page, position) => {
    index.set(page.path, position)
    const inbound = page.inbound ?? []
    // Seeded on a circle rather than at random: a deterministic start means the same
    // base lays out the same way twice, which matters when comparing screenshots.
    const angle = (position / Math.max(1, pages.length)) * Math.PI * 2
    const spread = 240 + ((position * 37) % 170)
    return {
      id: page.path,
      x: WIDTH / 2 + Math.cos(angle) * spread,
      y: HEIGHT / 2 + Math.sin(angle) * spread,
      vx: 0,
      vy: 0,
      inbound: inbound.length,
      orphan: orphans.has(page.path) || inbound.length === 0,
      broken: broken.has(page.path),
      group: groupOf(page.path),
    }
  })

  const edges: Edge[] = []
  const seen = new Set<string>()
  for (const page of pages) {
    const from = index.get(page.path)
    if (from === undefined) continue
    for (const target of page.outbound ?? []) {
      const to = index.get(target)
      if (to === undefined || to === from) continue
      const key = from < to ? `${from}-${to}` : `${to}-${from}`
      if (seen.has(key)) continue
      seen.add(key)
      edges.push({ from, to })
    }
  }
  return { nodes, edges, index }
}

/** The wiki subdirectory a page lives in, which is what its colour encodes. */
function groupOf(path: string): string {
  const segments = path.split('/')
  return segments.length >= 3 ? (segments[1] ?? 'root') : 'root'
}

function simulate(nodes: Node[], edges: Edge[], held: number | null): void {
  for (let i = 0; i < nodes.length; i += 1) {
    const a = nodes[i]
    if (!a) continue
    for (let j = i + 1; j < nodes.length; j += 1) {
      const b = nodes[j]
      if (!b) continue
      let dx = a.x - b.x
      let dy = a.y - b.y
      let distanceSquared = dx * dx + dy * dy
      if (distanceSquared < 1) {
        // Two nodes exactly on top of each other have no direction to separate in;
        // nudge deterministically rather than dividing by zero.
        dx = (i % 7) - 3 || 1
        dy = (j % 5) - 2 || 1
        distanceSquared = dx * dx + dy * dy
      }
      const force = REPULSION / distanceSquared
      const distance = Math.sqrt(distanceSquared)
      const fx = (dx / distance) * force
      const fy = (dy / distance) * force
      a.vx += fx
      a.vy += fy
      b.vx -= fx
      b.vy -= fy
    }
  }

  for (const edge of edges) {
    const a = nodes[edge.from]
    const b = nodes[edge.to]
    if (!a || !b) continue
    const dx = b.x - a.x
    const dy = b.y - a.y
    const distance = Math.max(1, Math.hypot(dx, dy))
    const force = (distance - SPRING_LENGTH) * SPRING
    const fx = (dx / distance) * force
    const fy = (dy / distance) * force
    a.vx += fx
    a.vy += fy
    b.vx -= fx
    b.vy -= fy
  }

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]
    if (!node) continue
    if (i === held) {
      node.vx = 0
      node.vy = 0
      continue
    }
    node.vx += (WIDTH / 2 - node.x) * CENTERING
    node.vy += (HEIGHT / 2 - node.y) * CENTERING
    node.vx *= DAMPING
    node.vy *= DAMPING
    node.x += node.vx
    node.y += node.vy
    // Keep everything inside the frame; an off-canvas hub is invisible and the
    // reader has no way to know it is there.
    node.x = Math.max(30, Math.min(WIDTH - 30, node.x))
    node.y = Math.max(26, Math.min(HEIGHT - 30, node.y))
  }
}
